import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../core/context/AuthContext";
import { initiateGoogleAuth } from "../../core/auth/google/googleAuth";
import LoggedInCTAButtons from "../../core/components/LoggedInCTAButtons";
import { GoogleIcon, ArrowRightIcon } from "../../design-system/icons";
import { APP_DOMAIN } from "../../core/utils/config";
import { submitPotentialCustomerLead } from "../../core/utils/leadsApi";
import { apiFetch } from "../../core/utils/api";

import LandingNavbar from "../landing/LandingNavbar";
import Testimonials from "../landing/Testimonials";
import FinalCTA from "../landing/FinalCTA";
import Footer from "../landing/Footer";
import useGlobalAccessGrantActive from "../landing/useGlobalAccessGrantActive";
import Website from "../../renderer/pages/Website";
import PreviewContext from "../../editor/context/PreviewContext";

import "./landing_v4.css";

const parseFeaturedWebsiteRoute = (route) => {
  const [username = "", domain = ""] = String(route || "")
    .split("/")
    .map((segment) => segment.trim())
    .filter(Boolean);

  return { username, domain };
};

const buildPreviewWebsite = (website, index) => {
  const { username, domain } = parseFeaturedWebsiteRoute(website?.url);
  if (!username || !domain) return null;

  return {
    ...website,
    previewKey: `${username}/${domain}-${index}`,
    username,
    domain,
    title: String(website?.name || "").trim() || `${username}/${domain}`,
  };
};

const LiveWebsitePreviewV4 = ({ website, scale = 0.25 }) => {
  const previewContextValue = {
    mode: "responsive",
    viewport: null,
    containerWidth: 1440,
    contentWidth: 1440,
    onNavigate: () => {},
    showFrame: false,
    manualZoom: 1,
  };

  return (
    <div className="bento-preview-wrapper">
      <PreviewContext.Provider value={previewContextValue}>
        <div className="bento-preview-inner" style={{ transform: `scale(${scale})` }}>
          <Website username={website.username} domain={website.domain} subdomain="" />
        </div>
      </PreviewContext.Provider>
    </div>
  );
};

const LandingHeroV4 = ({ isFreeGrantActive }) => {
  const [email, setEmail] = useState("");
  const { auth, isHydrated } = useContext(AuthContext);
  const navigate = useNavigate();
  const landingLeadSituation = "LANDING_EMAIL";
  
  const [websites, setWebsites] = useState([]);

  useEffect(() => {
    const fetchPublicWebsites = async () => {
      try {
        const response = await apiFetch("/api/websites/public/featured/");
        if (response.ok) {
          const data = await response.json();
          const parsed = (Array.isArray(data) ? data : [])
            .map((w, i) => buildPreviewWebsite(w, i))
            .filter(Boolean);
          setWebsites(parsed.slice(0, 3)); // We need exactly 3 for the bento grid
        }
      } catch (err) {
        console.warn("Failed to fetch featured public websites", err);
      }
    };
    fetchPublicWebsites();
  }, []);

  const handleCreateWebsite = () => {
    const trimmedEmail = (email || "").trim();
    const isValid = trimmedEmail.length > 0 && trimmedEmail.includes("@");
    if (isValid) {
      void submitPotentialCustomerLead(
        { email: trimmedEmail, situation: landingLeadSituation },
        { keepalive: true }
      ).catch((err) => {
        console.error("Failed to submit potential customer lead:", err);
      });

      const base = `/edit/register?email=${encodeURIComponent(trimmedEmail)}`;
      navigate(base);
    }
  };

  const handleSocialLogin = () => {
    initiateGoogleAuth('register');
  };

  const isEmailValid = email.trim().length > 0 && email.includes("@");

  // Fallbacks if websites haven't loaded
  const preview1 = websites[0];
  const preview2 = websites[1];
  const preview3 = websites[2];

  return (
    <div className="landing-v4-container">
      <div className="landing-v4-glow"></div>
      <LandingNavbar />
      
      <div className="bento-grid">
        {/* Box 1: Hero Content */}
        <div className="bento-item bento-hero">
          <div className="flex flex-col h-full justify-center">
            <h1 className="font-heading text-5xl lg:text-7xl font-bold tracking-tight mb-6 text-slate-900 dark:text-white transition-colors duration-300">
              Your work,<br/>
              <span className="text-blue-600 dark:text-blue-400">beautifully</span> boxed.
            </h1>
            <p className="text-xl text-slate-600 dark:text-dark-400 max-w-lg transition-colors duration-300">
              Create a personal website that makes you impossible to ignore. Stand out to recruiters, admissions, and the world.
              {isFreeGrantActive ? (
                <span className="text-green-600 dark:text-green-400 font-bold block mt-2">Start for free today.</span>
              ) : null}
            </p>
          </div>
        </div>

        {/* Box 2: Tall Preview */}
        <div className="bento-item bento-preview-1">
          {preview1 ? (
            <>
              <LiveWebsitePreviewV4 website={preview1} scale={0.28} />
              <div className="bento-preview-overlay">
                <h3 className="bento-preview-title">{preview1.title}</h3>
                <p className="bento-preview-domain">{preview1.domain}</p>
              </div>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-slate-100 dark:bg-dark-800 animate-pulse">
              <span className="text-slate-400 dark:text-dark-500">Loading...</span>
            </div>
          )}
        </div>

        {/* Box 3: CTA / Email Capture */}
        <div className="bento-item bento-cta">
          <div className="flex flex-col h-full justify-center">
            <h3 className="font-heading text-2xl font-bold mb-4 text-slate-900 dark:text-white transition-colors duration-300">Get Started</h3>
            {!isHydrated ? (
              <div className="h-24" />
            ) : auth && auth.userId ? (
              <LoggedInCTAButtons username={auth.username} />
            ) : (
              <form 
                className="flex flex-col gap-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  if (!isEmailValid) return;
                  handleCreateWebsite();
                }}
              >
                <input
                  type="email"
                  aria-label="Email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  className="bento-input"
                />
                <button
                  type="submit"
                  disabled={!isEmailValid}
                  className="bento-btn-primary"
                >
                  Start Building <ArrowRightIcon size="sm" />
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Box 4: Square Preview */}
        <div className="bento-item bento-preview-2">
          {preview2 ? (
            <>
              <LiveWebsitePreviewV4 website={preview2} scale={0.25} />
              <div className="bento-preview-overlay">
                <h3 className="bento-preview-title">{preview2.title}</h3>
                <p className="bento-preview-domain">{preview2.domain}</p>
              </div>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-slate-100 dark:bg-dark-800 animate-pulse">
              <span className="text-slate-400 dark:text-dark-500">Loading...</span>
            </div>
          )}
        </div>

        {/* Box 5: Social Login */}
        <div className="bento-item bento-social">
          <div className="w-full">
            <h3 className="font-heading text-xl font-bold mb-4 text-slate-900 dark:text-white text-center transition-colors duration-300">Quick Access</h3>
            <button
              type="button"
              onClick={handleSocialLogin}
              className="bento-btn-social"
            >
              <GoogleIcon size="sm" />
              Continue with Google
            </button>
          </div>
        </div>

        {/* Box 6: Wide Preview */}
        <div className="bento-item bento-preview-3">
          {preview3 ? (
            <>
              <LiveWebsitePreviewV4 website={preview3} scale={0.35} />
              <div className="bento-preview-overlay">
                <h3 className="bento-preview-title">{preview3.title}</h3>
                <p className="bento-preview-domain">{preview3.domain}</p>
              </div>
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-slate-100 dark:bg-dark-800 animate-pulse">
              <span className="text-slate-400 dark:text-dark-500">Loading...</span>
            </div>
          )}
        </div>

      </div>

      <div className="bg-white dark:bg-dark-950 relative z-10 transition-colors duration-300">
        <Testimonials />
        <FinalCTA isFreeGrantActive={isFreeGrantActive} />
        <Footer />
      </div>
    </div>
  );
};

const LandingV4 = () => {
  const isFreeGrantActive = useGlobalAccessGrantActive();

  return <LandingHeroV4 isFreeGrantActive={isFreeGrantActive} />;
};

export default LandingV4;
